<template>
    <div class="chart-wrapper">
        <div class="chart-header">
            <h5 class="chart-title">{{ title }}</h5>
        </div>
        <div class="card p-0">
            <!-- Loading overlay for both main loading and local loading -->
            <div v-if="isLoading || localLoading" class="chart-overlay">
                <div class="spinner">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                        <span class="sr-only">Cargando...</span>
                    </div>
                </div>
            </div>
            <div v-else-if="showInfoButton" class="info-icon">
                <button class="btn info-btn" @click="handleInfoClick">
                    <i class="fa fa-info-circle"></i>
                </button>
            </div>
            <div class="card-body p-0">
                <div v-if="hasData" 
                     ref="chart" 
                     :data-chart-key="chartKey" 
                     style="height: 300px; width: 100%;" 
                     :class="{ 'chart-loading': isLoading || localLoading }">
                </div>
                <div v-else class="no-data-message">
                    <p>{{ messageNoData }}</p>
                </div>

                <!-- Controles de paginación -->
                <div v-if="showPagination" class="pagination-controls mt-3">
                    <button class="btn btn-light btn-sm mr-2" 
                            :disabled="currentPage === 1"
                            @click="changePage(currentPage - 1)">
                        <i class="fa fa-chevron-left"></i>
                    </button>
                    <span class="mx-2">
                        Página {{ currentPage }} de {{ totalPages }}
                    </span>
                    <button class="btn btn-light btn-sm ml-2" 
                            :disabled="currentPage === totalPages"
                            @click="changePage(currentPage + 1)">
                        <i class="fa fa-chevron-right"></i>
                    </button>
                </div>

                <!-- mensaje de información  si se manda true -->
                <div v-if="showMessageInfo" class="info-message">
                    <p>{{ messageInfo }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
    name: 'ChartCard',
    props: {
        title: {
            type: String,
            required: true
        },
        options: {
            type: Object,
            required: true
        },
        isPaginated: {
            type: Boolean,
            default: false
        },
        fullData: {
            type: Array,
            default: () => []
        },
        itemsPerPage: {
            type: Number,
            default: 10
        },
        chartDescription: {
            type: String,
            default: 'Información detallada sobre el gráfico.'
        },
        chartKey: {
            type: String,
            required: true
        },
        showInfoButton: {
            type: Boolean,
            default: true
        },
        isLoading: { 
            type: Boolean, 
            default: false 
        },
        messageNoData: {
            type: String,
            default: 'No hay datos disponibles para mostrar el gráfico.'
        },
        messageInfo: {
            type: String,
            default: 'Nota: un producto puede estar clasificado en múltiples categorías de peligro.'
        },
        showMessageInfo: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            currentPage: 1,
            chart: null,
            showInfoIcon: false,
            localLoading: false
        }
    },
    computed: {
        hasData() {
            return this.options && 
                   typeof this.options === 'object' && 
                   Object.keys(this.options).length > 0 &&
                   this.options.series &&
                   this.options.series.some(series => series.data && series.data.length > 0);
        },
        showPagination() {
            return this.isPaginated && this.fullData.length > this.itemsPerPage;
        },
        totalPages() {
            return Math.ceil(this.fullData.length / this.itemsPerPage);
        }
    },
    watch: {
        isLoading() {
            this.localLoading = this.isLoading;
        },
        options: {
            handler(newVal) {
                if (this.hasData && this.chart) {
                    this.chart.setOption(newVal);
                }
            },
            deep: true
        }
    },
    methods: {
        async handleInfoClick() {
            await this.$emit('chart-info-click', {
                key: this.chartKey,
                title: this.title
            });
        },
        initChart() {
            if (!this.chart) {
                this.chart = echarts.init(this.$refs.chart);
            }
            this.chart.setOption(this.options);
            
            // Add click event handler for the chart
            this.chart.on('click', async (params) => {
              
                try {
                    await this.$emit('chart-segment-clicked', params.data);
                } finally {
                    console.log(params);
                }
            });
            
            window.addEventListener('resize', this.handleResize);
        },
        handleResize() {
            if (this.chart) {
                this.chart.resize();
            }
        },
        changePage(newPage) {
            this.currentPage = newPage;
            this.$emit('page-changed', newPage);
        }
    },
    mounted() {
        if (this.hasData) {
            this.initChart();
        }
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    }
}
</script>

<style scoped>
.chart-wrapper {
    position: relative;
}

.chart-header {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
}

.chart-title {
    position: relative;
    margin-bottom: 0;
    font-size: 18px;
    font-weight: 800;
    color: #1B212E;
    z-index: 1;
}

.card {
    position: relative;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 25px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}

.info-icon {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
}

.info-btn {
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.info-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    background: white;
}

.info-btn i {
    color: #4a5568;
    font-size: 1.1rem;
    transition: color 0.3s ease;
}

.info-btn:hover i {
    color: #2d3748;
}

.spinner {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.spinner-border {
    width: 1.2rem;
    height: 1.2rem;
    border-width: 0.15em;
}

.chart-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 100;
    transition: opacity 0.3s ease;
}

.chart-loading {
    opacity: 0.6;
    pointer-events: none;
    transition: opacity 0.3s ease;
}

.card-body {
    padding: 0.5rem !important;
    font-family: 'Poppins', sans-serif;
    font-weight: 300;
    color: #1B212E;
}

.no-data-message {
    text-align: center;
    color: #718096;
    padding: 3rem 0;
    height: 300px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pagination-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
}

.btn-light {
    background-color: #f8f9fa;
    border-color: #e2e8f0;
    transition: all 0.2s ease;
}

.btn-light:hover:not(:disabled) {
    background-color: #e9ecef;
    transform: translateY(-1px);
}

.btn-light:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.mx-2 {
    color: #4a5568;
    font-size: 0.875rem;
}

/* Fade transition for loading states */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.info-message {
    text-align: left;
    color: #666;
    padding: 0.5rem;
    font-size: 12px;
}
</style>